import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';

import { AclAccess, FsAcl } from '@firestitch/acl';
import { FsSidenavComponent, FsSidenavModule, Item, ItemActionType } from '@firestitch/sidenav';

import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { AclPermission } from '@common/enums';
import { SessionService } from '@common/services';

import { ObjectDrawerService } from '@libs/drawers/drawer';
import { GlobalSearchComponent } from '@libs/global-search';
import { TaskCreateComponent } from '@libs/task-create';
import { TimerComponent } from '@libs/timer';

import { ProjectSidenavService } from '@app/common';
import { TimerService } from '@app/time-entry';
import { MeetingJoinService } from '@app/meetings/services';

import { SidenavConfig, SidenavItemConfig } from '../../../interfaces';
import { SidenavService } from '../../../services';

import { SearchProjectComponent } from './search-project/search-project.component';


@Component({
  selector: 'app-page-side-nav-main',
  templateUrl: './side-nav-main.component.html',
  styleUrls: ['./side-nav-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FsSidenavModule,
  ],
})
export class SideNavMainComponent implements OnInit, OnDestroy {

  @ViewChild(MatSidenav, { static: true })
  public sidenav: MatSidenav;

  @ViewChild(FsSidenavComponent, { static: true })
  public sideNavMainEl: FsSidenavComponent;

  @ViewChild('notifications', { static: true })
  public notificationsTemplate: TemplateRef<any>;

  public sideNavConfig: SidenavConfig = { items: [] };

  private _timerSidenavItem: Item = null;
  private _destroy$ = new Subject<void>();
  private _sidenavService = inject(SidenavService);
  private _dialog = inject(MatDialog);
  private _router = inject(Router);
  private _acl = inject(FsAcl);
  private _timerService = inject(TimerService);
  private _objectDrawerService = inject(ObjectDrawerService);
  private _meetingJoinService = inject(MeetingJoinService);
  private _cdRef = inject(ChangeDetectorRef);
  private _projectSidenavService = inject(ProjectSidenavService);
  private _sessionService = inject(SessionService);
  
  constructor(
  ) {
    this._timerSidenavItem = {
      label: 'Timer',
      icon: 'timer',
      class: 'timer',
      menu: {
        component: TimerComponent,
        offsetX: 10,
      },
    };

    this._projectSidenavService.project$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadMainSidenav();
      });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => window.location.pathname),
        distinctUntilChanged(),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadMainSidenav();
      });
  }

  public ngOnInit(): void {
    this._timerService.time$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((response: any) => {
        this._timerSidenavItem.label = 'Timer';
        this._timerSidenavItem.class = 'timer-stopped';

        if (response) {
          this._timerSidenavItem.label = response;
          this._timerSidenavItem.class = 'timer-started';
        }

        if (this.sideNavMainEl) {
          this.sideNavMainEl.detectChanges();
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _loadMainSidenav(): void {
    let projectSidenavItem: SidenavItemConfig = { hide: true };
    const project = this._projectSidenavService.project;

    if (project) {
      projectSidenavItem = {
        superlabel: 'Project',
        label: project.name,
        path: `/projects/${project.id}`,
        class: 'project',
        exact: true,
        hide: false,
        items: [
          {
            label: 'Tasks',
            path: `/projects/${project.id}/tasks`,
            exact: false,
            permissions: [
              { permission: AclPermission.Task, object: project.environmentId },
              { permission: AclPermission.Task, object: project.id },
            ],
          },
          {
            label: 'Board',
            path: `/projects/${project.id}/board`,
            exact: false,
            permissions: [
              { permission: AclPermission.Task, object: project.environmentId },
              { permission: AclPermission.Task, object: project.id },
            ],
          },
          {
            label: 'Plans',
            path: `/projects/${project.id}/plans`,
            paths: [/projects\/\d+\/components/],
            exact: false,
            permissions: [
              { permission: AclPermission.Plan, object: project.environmentId },
              { permission: AclPermission.Plan, object: project.id },
            ],
          },
          {
            label: 'Docs',
            path: `/projects/${project.id}/docs`,
            exact: false,
            permissions: [
              { permission: AclPermission.Doc, object: project.environmentId },
              { permission: AclPermission.Doc, object: project.id },
            ],
          },
          { label: 'Mockups', path: `/projects/${project.id}/images`, exact: false,
            permissions: [
              { permission: AclPermission.Image, object: project.environmentId },
              { permission: AclPermission.Image, object: project.id },
            ],
          },
          { label: 'Designs', path: `/projects/${project.id}/design`, exact: false,
            permissions: [
              { permission: AclPermission.Design, object: project.environmentId },
              { permission: AclPermission.Design, object: project.id },
            ],
          },
          { 
            label: 'Data', 
            path: `/projects/${project.id}/data`, 
            exact: false,
            permissions: [
              { permission: AclPermission.Data, object: project.environmentId },
              { permission: AclPermission.Data, object: project.id },
            ],
          },
          { 
            label: 'Scenarios', 
            path: `/projects/${project.id}/scenarios`, 
            exact: false,
            permissions: [
              { permission: AclPermission.Scenario, object: project.environmentId },
              { permission: AclPermission.Scenario, object: project.id },
            ],
          },
          { 
            label: 'KB', 
            path: `/projects/${project.id}/kbs`, 
            exact: false,
            permissions: [
              { permission: AclPermission.Kb, object: project.environmentId },
              { permission: AclPermission.Kb, object: project.id },
            ],
          },
          { 
            label: 'Test Cases', 
            path: `/projects/${project.id}/testcases`, 
            exact: false,
            permissions: [
              { permission: AclPermission.TestCase, object: project.environmentId },
              { permission: AclPermission.TestCase, object: project.id },
            ],
          },
          {
            label: 'Artifacts',
            path: `/projects/${project.id}/artifacts`,
            exact: false,
            permissions: [
              { object: project.environmentId },
              { object: project.id },
            ],
          },
          {
            label: 'Meetings',
            path: `/projects/${project.id}/meetings`,
            exact: false,
            permissions: [
              { permission: AclPermission.Meeting, object: project.environmentId },
              { permission: AclPermission.Meeting, object: project.id },
            ],
            actions: [
              {
                type: ItemActionType.Button,
                icon: 'add',
                // Same Join flow as the meetings list: the add dialog is followed
                // by the project's meetings list and the bot going out in an
                // FsProcess entry.
                click: () => this._meetingJoinService.join(project.id),
              },
            ]
              .filter(() => this._acl.has({
                permission: AclPermission.Meeting,
                access: AclAccess.Write,
                object: project.id,
              })),
          },
          {
            label: 'Settings',
            path: `/projects/${project.id}/settings`,
            exact: false,
            permissions: [
              { permission: AclPermission.Environment, object: project.environmentId, access: AclAccess.Full },
              { permission: AclPermission.Project, object: project.id, access: AclAccess.Full  },
            ],
          },
        ],
      };
    }

    const items: SidenavItemConfig[] = [
      this._timerSidenavItem,
      {
        label: 'Search',
        icon: 'search',
        click: () => {
          this._dialog.open(GlobalSearchComponent, {
            ...GlobalSearchComponent.DialogConfig,
            data: {
              jumboSearch: true,
            },
          });
        },
      },
      {
        label: 'Projects',
        path: '/projects',
        icon: 'folder',
        actions: [
          {
            type: ItemActionType.Menu,
            icon: 'more_vert',
            menu: {
              component: SearchProjectComponent,
              panelClass: 'sidenav-menu-project',
              offsetY: -13,
            },
          },
        ],
      },
      projectSidenavItem,
      {
        label: 'Plans',
        path: '/projects/plans',
        icon: 'assignment_turned_in',
        paths: [/projects\/components/],
      },
      {
        label: 'Tasks',
        path: '/projects/tasks',
        icon: 'assignment',
        actions: [
          {
            type: ItemActionType.Button,
            icon: 'add',
            click: () => {
              this._dialog.open(TaskCreateComponent, {
                data: {},
                autoFocus: false,
              })
                .afterClosed()
                .pipe(
                  takeUntil(this._destroy$),
                )
                .subscribe((task) => {
                  if (task) {
                    this._objectDrawerService.openObject(
                      task.identifier,
                      { closeAll: false },
                    );
                  }
                });
            },
          },
        ]
          .filter(() => {
            return this._acl
              .has({
                permission: AclPermission.Task,
                access: AclAccess.Write,
              });
          }),
      },
      {
        label: 'Meetings',
        path: '/projects/meetings',
        icon: 'video_chat',
        permissions: [
          AclPermission.Meeting,
        ],
        actions: [
          {
            type: ItemActionType.Button,
            icon: 'add',
            // Same Join flow as the meetings list: the add dialog is followed
            // by the meetings list and the bot going out in an FsProcess entry.
            click: () => this._meetingJoinService.join(),
          },
        ]
          .filter(() => this._acl.has({
            permission: AclPermission.Meeting,
            access: AclAccess.Write,
          })),
        items: [
          {
            label: 'People',
            path: '/projects/meeting-persons',
          },
        ],
      },
      {
        label: 'Reports',
        path: '/reports',
        icon: 'assessment',
        items: [
          {
            label: 'Time Entries',
            path: '/reports/timeentries',
          },
          {
            label: 'Time',
            path: '/reports/time',
            exact: true,
            permissions: [
              AclPermission.Admin,
            ],
          },
          {
            label: 'Actuals',
            path: '/reports/actuals',
            permissions: [
              AclPermission.Admin,
            ],
          },
        ],
      },
    ]
      .filter((item: SidenavItemConfig) => {
        return !item.permissions || this._acl.has(item.permissions);
      })
      .reduce((accum, item: SidenavItemConfig) => {
        item.items = (item.items || [])
          .filter((itemItem: SidenavItemConfig) => {
            return !itemItem.permissions || this._acl.has(itemItem.permissions);
          });

        return [
          ...accum,
          item,
        ];
      }, []);

    this.sideNavConfig = {
      items,
      itemClick: ({ item }) => {
        if(item && !(item.items || []).length) {
          this._sidenavService.hideMobile();
        }
      },
    };

    this._cdRef.markForCheck();
  }

}

